import { getFirestore, doc, getDoc } from 'firebase/firestore';

/**
 * Récupère le connectedAccountId d'un utilisateur depuis Firestore
 * @param userId - L'ID de l'utilisateur propriétaire
 * @returns Le connectedAccountId ou null si non trouvé
 */
export async function getConnectedAccountId(userId: string): Promise<string | null> {
  try {
    const db = getFirestore();
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      console.warn(`User ${userId} not found`);
      return null;
    }
    
    const userData = userDoc.data();
    const connectedAccountId = userData?.stripe?.connectedAccountId || null;
    
    return connectedAccountId;
  } catch (error) {
    console.error('Error fetching connectedAccountId:', error);
    return null;
  }
}

/**
 * Vérifie si un utilisateur a un compte Stripe Connect activé
 * @param userId - L'ID de l'utilisateur propriétaire
 * @returns true si le compte est activé, false sinon
 */
export async function isStripeAccountActive(userId: string): Promise<boolean> {
  try {
    const db = getFirestore();
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data();
    const stripe = userData?.stripe || {};
    
    return !!(
      stripe.connectedAccountId &&
      stripe.chargesEnabled &&
      stripe.payoutsEnabled &&
      stripe.onboardingCompleted
    );
  } catch (error) {
    console.error('Error checking Stripe account status:', error);
    return false;
  }
}

/**
 * Récupère les informations complètes Stripe d'un utilisateur
 * @param userId - L'ID de l'utilisateur propriétaire
 * @returns Les informations Stripe ou null
 */
export async function getStripeInfo(userId: string): Promise<{
  connectedAccountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  onboardingCompleted: boolean;
} | null> {
  try {
    const db = getFirestore();
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      return null;
    }
    
    const userData = userDoc.data();
    const stripe = userData?.stripe || {};
    
    return {
      connectedAccountId: stripe.connectedAccountId || null,
      chargesEnabled: stripe.chargesEnabled || false,
      payoutsEnabled: stripe.payoutsEnabled || false,
      detailsSubmitted: stripe.detailsSubmitted || false,
      onboardingCompleted: stripe.onboardingCompleted || false
    };
  } catch (error) {
    console.error('Error fetching Stripe info:', error);
    return null;
  }
}

